import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { Spacer, FieldSet, Flex, Label, Popper, FormHelp, AdornedInput, Adornment, Input, Popover, Heading, Paragraph } from '@zillow/constellation';
import React, { useRef, useEffect } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import useValidFieldUpdate from '../../utils/form/useValidFieldUpdate.js';
import { fetchRate } from '../../utils/mortgagehelpers/mortgage-helpers.js';
import { getCommaSeparatedNumber, commaSeparateNumber } from '../../utils/zhl-calculator.js';
import { sendFieldInteractionEvent } from './VALoanCalculator.Clickstream.js';
import { FieldConfig, isSameFloat } from './VALoanCalculator.utils.js';

function DownPaymentField(_ref) {
  var _proxyFormState$error, _proxyFormState$error2;
  var _ref$isFHAEnabled = _ref.isFHAEnabled,
    isFHAEnabled = _ref$isFHAEnabled === void 0 ? false : _ref$isFHAEnabled,
    _ref$isMortgageCompon = _ref.isMortgageComponent,
    isMortgageComponent = _ref$isMortgageCompon === void 0 ? false : _ref$isMortgageCompon;
  var form = useFormContext();
  var proxyFormState = useFormState();
  var error = (proxyFormState === null || proxyFormState === void 0 || (_proxyFormState$error = proxyFormState.errors[FieldConfig.downPayment.name]) === null || _proxyFormState$error === void 0 ? void 0 : _proxyFormState$error.message) || (proxyFormState === null || proxyFormState === void 0 || (_proxyFormState$error2 = proxyFormState.errors[FieldConfig.downPaymentPercent.name]) === null || _proxyFormState$error2 === void 0 ? void 0 : _proxyFormState$error2.message);
  var fieldAmount = form.register(FieldConfig.downPayment.name, FieldConfig.downPayment.config);
  var fieldPercent = form.register(FieldConfig.downPaymentPercent.name, FieldConfig.downPaymentPercent.config);
  var amountInputRef = useRef(null);
  var formatAmount = commaSeparateNumber;

  // Format on mount
  useEffect(function () {
    if (formatAmount) {
      var defaultAmountValue = form.getValues(fieldAmount.name);
      if (defaultAmountValue) {
        amountInputRef.current.value = formatAmount(defaultAmountValue);
      }
    }
  }, []);
  var trigger = isFHAEnabled ? /*#__PURE__*/React.createElement(Popover, {
    header: /*#__PURE__*/React.createElement(Heading, {
      level: 6
    }, "Down payment"),
    body: /*#__PURE__*/React.createElement(Paragraph, null, "The minimum down payment for FHA loans is typically 3.5% with a credit score of 580 and higher and 10% for credit scores between 500 to 579.")
  }) : false;
  useSyncDownPaymentPercent(isFHAEnabled, isMortgageComponent);
  return /*#__PURE__*/React.createElement(Spacer, {
    marginBottom: "xs"
  }, /*#__PURE__*/React.createElement(FieldSet, {
    legend: /*#__PURE__*/React.createElement(Flex, {
      display: "flex",
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(Label, {
      htmlFor: "downPaymentAmount"
    }, "Down payment"), isFHAEnabled && /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0", /*#__PURE__*/React.createElement(Popper, {
      triggered: trigger
    }))),
    description:
    /*#__PURE__*/
    // Use blank space when no error or description to avoid
    // layout shift when when error is shown.
    React.createElement(FormHelp, {
      error: Boolean(error)
    }, error || /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0")),
    required: true
  }, /*#__PURE__*/React.createElement(Flex, {
    display: "flex"
  }, /*#__PURE__*/React.createElement(AdornedInput, {
    style: {
      flex: '1'
    },
    error: Boolean(error),
    leftAdornment: /*#__PURE__*/React.createElement(Adornment, null, "$"),
    input: /*#__PURE__*/React.createElement(Input, _extends({
      style: {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        borderRightWidth: 0
      },
      id: "downPaymentAmount"
    }, fieldAmount, {
      ref: function ref(el) {
        amountInputRef.current = el;
        fieldAmount.ref(el);
      },
      onInput: formatAmount ? function (e) {
        e.target.value = formatAmount(e.target.value);
      } : undefined,
      autoComplete: "off"
    }))
  }), /*#__PURE__*/React.createElement(AdornedInput, {
    style: {
      flex: '0.5'
    },
    error: Boolean(error),
    rightAdornment: /*#__PURE__*/React.createElement(Adornment, null, "%"),
    input: /*#__PURE__*/React.createElement(Input, _extends({}, fieldPercent, {
      autoComplete: "off",
      style: {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0
      }
    }))
  }))));
}
function useSyncDownPaymentPercent(isFHAEnabled, isMortgageComponent) {
  var form = useFormContext();

  // Watch for changes and do side effects
  var lastValidFields = useRef(null);
  if (lastValidFields.current === null) {
    lastValidFields.current = form.getValues();
  }
  useValidFieldUpdate(form, function (name, value) {
    return new Promise(function ($return, $error) {
      var _Object$assign, nextDownPayment, _nextDownPayment, _nextDownPayment2, newRate;
      lastValidFields.current = Object.assign({}, lastValidFields.current, (_Object$assign = {}, _Object$assign[name] = value, _Object$assign));

      // 1) on change of downPayment, update downPaymentPercent based  on homeprice
      if (name === FieldConfig.downPayment.name) {
        nextDownPayment = lastValidFields.current[FieldConfig.downPayment.name] / lastValidFields.current[FieldConfig.homePrice.name] * 100;
        if (!isSameFloat(nextDownPayment, lastValidFields.current.downPaymentPercent)) {
          form.setValue(FieldConfig.downPaymentPercent.name, nextDownPayment, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true
          });
        }
        if (!isFHAEnabled) {
          sendFieldInteractionEvent('Down payment amount', 'form_field');
        }
      }

      // 2) on change of downPaymentPercent, update downPayment based on downPaymentPercent
      if (name === FieldConfig.downPaymentPercent.name) {
        _nextDownPayment = lastValidFields.current[FieldConfig.downPaymentPercent.name] / 100 * lastValidFields.current[FieldConfig.homePrice.name];
        if (!isSameFloat(_nextDownPayment, lastValidFields.current.downPayment)) {
          form.setValue(FieldConfig.downPayment.name, getCommaSeparatedNumber(_nextDownPayment), {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true
          });
        }
        if (!isFHAEnabled) {
          sendFieldInteractionEvent('Down payment percent', 'form_field');
        }
      }

      // 3) on change of homePrice, update downPaymentPercent based on downPayment
      if (name === FieldConfig.homePrice.name) {
        _nextDownPayment2 = lastValidFields.current[FieldConfig.downPayment.name] / lastValidFields.current[FieldConfig.homePrice.name] * 100;
        if (!isSameFloat(_nextDownPayment2, lastValidFields.current.downPaymentPercent)) {
          form.setValue(FieldConfig.downPaymentPercent.name, _nextDownPayment2, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true
          });
        }
      }

      /* 4) on change any of the fields from homeprice, downpPayment, downPaymentpercent and loanProgram 
      set the interest rate fetched from API */
      if (name === FieldConfig.downPayment.name || name === FieldConfig.homePrice.name || name === FieldConfig.downPaymentPercent.name || name === (isFHAEnabled ? FieldConfig.fhaLoanProgram.name : FieldConfig.loanProgram.name)) {
        return Promise.resolve(fetchRate({
          homePrice: lastValidFields.current[FieldConfig.homePrice.name],
          downPayment: Math.round(lastValidFields.current[FieldConfig.downPayment.name]),
          va: isFHAEnabled ? false : isMortgageComponent ? false : true,
          fha: isFHAEnabled ? true : false,
          loanProgram: lastValidFields.current[FieldConfig.loanProgram.name]
        })).then(function ($await_2) {
          try {
            newRate = $await_2;
            if (!isSameFloat(newRate, lastValidFields.current.interestRate)) {
              form.setValue(FieldConfig.interestRate.name, newRate, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true
              });
            }
            return $If_1.call(this);
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }.bind(this), $error);
      }
      function $If_1() {
        return $return();
      }
      return $If_1.call(this);
    });
  });
}

export { DownPaymentField as default };
